import { useCallback, useState } from "react";
import { ChevronDown, Mail, MessageCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { useTenantIssueHistory } from "@/hooks/useTenantIssueHistory";
import {
  SUPPORT_CONTACT,
  SUPPORT_CONTACT_WHATSAPP_URL,
} from "@/lib/support-contact";
import {
  getApplicantFaq,
  getHelpScreenLabels,
  getTenantFaq,
  translateFaq,
} from "@/lib/tenant-help-copy";

type Props = {
  applicant: boolean;
};

/**
 * Tenant Help without the retired in-portal ticket channel.
 *
 * New conversations enter the unified inbox through its supported delivery
 * channels (Email or WhatsApp). The FAQ remains in the portal, while legacy
 * ticket rows are left intact for staff-side history and auditability.
 */
export default function TenantSupportHelp({ applicant }: Props) {
  const { language: activeLanguage, tDict } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const {
    data: requests = [],
    isPending: requestsPending,
    isError: requestsFailed,
  } = useTenantIssueHistory("all");
  const tt = useCallback(
    (key: string, literal?: string) =>
      tDict(key, literal ?? "", activeLanguage),
    [activeLanguage, tDict]
  );
  const base = translateDictionary(
    "tenant.help",
    getHelpScreenLabels(language),
    tt
  );
  const faq = applicant
    ? translateFaq("tenant.help.applicantFaq", getApplicantFaq(language), tt)
    : translateFaq("tenant.help.faq", getTenantFaq(language), tt);
  const copy =
    language === "de"
      ? {
          heading: "Support kontaktieren",
          body: "Schreib uns per WhatsApp oder E-Mail. Deine Nachricht erscheint direkt im Support-Posteingang.",
          email: "E-Mail schreiben",
          whatsapp: "WhatsApp öffnen",
          requestsHeading: "Deine Anfragen",
          requestsEmpty: "Keine bisherigen Anfragen.",
          requestsLoading: "Anfragen werden geladen …",
          requestsFailed: "Anfragen konnten nicht geladen werden.",
          statusOpen: "Offen",
          statusInProgress: "In Bearbeitung",
          statusWaiting: "Wartet",
          statusResolved: "Gelöst",
          statusClosed: "Geschlossen",
        }
      : {
          heading: "Contact support",
          body: "Message us by WhatsApp or email. Your message goes straight to the Support inbox.",
          email: "Write an email",
          whatsapp: "Open WhatsApp",
          requestsHeading: "Your requests",
          requestsEmpty: "No previous requests.",
          requestsLoading: "Loading requests …",
          requestsFailed: "Requests could not be loaded.",
          statusOpen: "Open",
          statusInProgress: "In progress",
          statusWaiting: "Waiting",
          statusResolved: "Resolved",
          statusClosed: "Closed",
        };
  const c = {
    heading: tt("tenant.help.contact.heading", copy.heading),
    body: tt("tenant.help.contact.body", copy.body),
    email: tt("tenant.help.contact.email", copy.email),
    whatsapp: tt("tenant.help.contact.whatsapp", copy.whatsapp),
    requestsHeading: tt("tenant.help.requests.heading", copy.requestsHeading),
    requestsEmpty: tt("tenant.help.requests.empty", copy.requestsEmpty),
    requestsLoading: tt("tenant.help.requests.loading", copy.requestsLoading),
    requestsFailed: tt("tenant.help.requests.failed", copy.requestsFailed),
    statusOpen: tt("tenant.help.requests.status.open", copy.statusOpen),
    statusInProgress: tt(
      "tenant.help.requests.status.inProgress",
      copy.statusInProgress
    ),
    statusWaiting: tt(
      "tenant.help.requests.status.waiting",
      copy.statusWaiting
    ),
    statusResolved: tt(
      "tenant.help.requests.status.resolved",
      copy.statusResolved
    ),
    statusClosed: tt("tenant.help.requests.status.closed", copy.statusClosed),
  };
  const statusLabel = (status: string) =>
    ({
      open: c.statusOpen,
      in_progress: c.statusInProgress,
      waiting: c.statusWaiting,
      resolved: c.statusResolved,
      closed: c.statusClosed,
    })[status] ?? status;
  const requestDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-GB", {
      dateStyle: "medium",
    }).format(date);
  };

  return (
    <div className="space-y-8">
      {/* `data-search-target`: where dashboard search results land (COL-623). */}
      <Card
        data-search-target="help-request"
        className="rounded-[20px] p-6 sm:p-8"
      >
        <h2 className="text-[22px] font-semibold leading-[1.2] tracking-[-0.03em] text-foreground">
          {c.heading}
        </h2>
        <p className="mt-2 max-w-2xl text-[15px] leading-[1.5] text-muted-foreground">
          {c.body}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href={SUPPORT_CONTACT_WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            {c.whatsapp}
          </a>
          <a
            href={`mailto:${SUPPORT_CONTACT.email}`}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            {c.email}
          </a>
        </div>
      </Card>

      <Card className="rounded-[20px] p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-foreground">
          {c.requestsHeading}
        </h2>
        {requestsPending || requestsFailed ? (
          <p className="mt-4 text-sm text-muted-foreground" role="status">
            {requestsFailed ? c.requestsFailed : c.requestsLoading}
          </p>
        ) : requests.length ? (
          <ul className="mt-4 divide-y divide-border/60">
            {requests.map((request) => (
              <li key={request.id} className="py-4 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-foreground">
                      {request.title}
                    </h3>
                    {request.description ? (
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                        {request.description}
                      </p>
                    ) : null}
                  </div>
                  <span className="shrink-0 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                    {statusLabel(request.status)}
                  </span>
                </div>
                <time
                  className="mt-2 block text-xs text-muted-foreground"
                  dateTime={request.last_message_at || request.created_at}
                >
                  {requestDate(request.last_message_at || request.created_at)}
                </time>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            {c.requestsEmpty}
          </p>
        )}
      </Card>

      <Card
        data-search-target="help-faq"
        className="rounded-[20px] border-0 bg-muted/60 p-6 sm:p-8"
      >
        <h2 className="text-lg font-semibold text-foreground">
          {base.faqHeading}
        </h2>
        <ul className="mt-4 divide-y divide-border/60">
          {faq.map((entry, index) => {
            const open = faqOpen === index;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => setFaqOpen(open ? null : index)}
                  className="flex w-full items-center justify-between gap-4 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="font-semibold text-foreground">
                    {entry.q}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {open ? (
                  <p className="pb-4 text-sm text-muted-foreground">
                    {entry.a}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
